import { useState } from 'react';

import { useVoiceCopy } from '@/platform/voice/use-voice';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiRequestError } from '@/lib/api';
import { useOwnProfile, useUpdateOwnProfile } from '@/platform/features/users/use-own-profile';
import { usePublicBrand } from '@/platform/brand/use-public-brand';
import { ThemePreview } from '@/platform/features/profile/appearance-choices';
import {
  COLOR_MODE_OPTIONS,
  VARIANT_OPTIONS,
} from '@/platform/features/profile/appearance-options';
import {
  APPEARANCE_STORAGE_KEY,
  DEFAULT_APPEARANCE,
  PREFERS_DARK_QUERY,
  resolveAppearance,
  type Appearance,
  type ColorMode,
  type DesignVariant,
} from '@/platform/theme/appearance';
import { useResolvedColorScheme } from '@/platform/theme/appearance-provider';
import { applyAppearance } from '@/platform/theme/apply-appearance';
import { readAppearance, writeAppearance } from '@/platform/theme/appearance-storage';

function browserStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function systemPrefersDark(): boolean {
  try {
    return window.matchMedia(PREFERS_DARK_QUERY).matches;
  } catch {
    return false;
  }
}

function publishAppearance(next: Appearance) {
  writeAppearance(browserStorage(), next);
  applyAppearance(document.documentElement, next, systemPrefersDark());
  window.dispatchEvent(new StorageEvent('storage', { key: APPEARANCE_STORAGE_KEY }));
}

function appearanceFromProfile(
  profile: { colorMode: ColorMode | null; designVariant: DesignVariant | null } | undefined,
  brand: Appearance,
): Appearance {
  if (!profile) {
    return readAppearance(browserStorage());
  }
  return resolveAppearance(profile, brand);
}

export function ProfileAppearanceControls() {
  const copy = useVoiceCopy();
  const profile = useOwnProfile();
  const brandQuery = usePublicBrand();
  const update = useUpdateOwnProfile();
  const brandAppearance: Appearance = {
    colorMode: brandQuery.data?.colorMode ?? DEFAULT_APPEARANCE.colorMode,
    designVariant: brandQuery.data?.designVariant ?? DEFAULT_APPEARANCE.designVariant,
  };
  const resolved = useResolvedColorScheme();
  const [appearance, setAppearance] = useState<Appearance>(() => readAppearance(browserStorage()));
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [syncedKey, setSyncedKey] = useState<string | null>(null);

  const profileKey = profile.data
    ? `${profile.data.colorMode}:${profile.data.designVariant}:${brandAppearance.colorMode}:${brandAppearance.designVariant}`
    : null;

  // Sync local controls from the loaded profile (React: adjust state while rendering).
  if (!saving && profileKey !== null && profileKey !== syncedKey) {
    setSyncedKey(profileKey);
    setAppearance(appearanceFromProfile(profile.data, brandAppearance));
  }

  async function restoreTenant() {
    const previous = appearance;
    setSaving(true);
    setAppearance(brandAppearance);
    setStatus('Wird gespeichert.');
    publishAppearance(brandAppearance);
    try {
      await update.mutateAsync({ colorMode: null, designVariant: null });
      setStatus('Übernommen.');
    } catch (error) {
      setAppearance(previous);
      publishAppearance(previous);
      const message =
        error instanceof ApiRequestError
          ? error.message
          : 'Das Erscheinungsbild konnte nicht gespeichert werden.';
      setStatus(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  async function commit(next: Appearance) {
    const previous = appearance;
    setSaving(true);
    setAppearance(next);
    setStatus('Wird gespeichert.');
    publishAppearance(next);
    try {
      await update.mutateAsync({
        colorMode: next.colorMode,
        designVariant: next.designVariant,
      });
      setStatus('Übernommen.');
    } catch (error) {
      setAppearance(previous);
      publishAppearance(previous);
      const message =
        error instanceof ApiRequestError
          ? error.message
          : 'Das Erscheinungsbild konnte nicht gespeichert werden.';
      setStatus(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Erscheinungsbild</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <fieldset className="space-y-3" aria-describedby="farbmodus-hilfe">
          <legend className="text-sm font-medium">Farbmodus</legend>
          <p id="farbmodus-hilfe" className="text-sm text-muted-foreground">
            {copy('platform.profile.appearance.system')}
          </p>
          <div className="flex flex-col gap-2">
            {COLOR_MODE_OPTIONS.map((option) => {
              const checked = appearance.colorMode === option.value;
              return (
                <label
                  key={option.value}
                  className="flex min-w-0 cursor-pointer items-center gap-3 rounded-md border border-border px-3 py-2 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring"
                >
                  <input
                    type="radio"
                    name="color-mode"
                    value={option.value}
                    checked={checked}
                    onChange={() => {
                      void commit({ ...appearance, colorMode: option.value });
                    }}
                  />
                  <span className="min-w-0 text-sm">{option.label}</span>
                  {checked ? <span className="ml-auto text-sm font-medium">Aktiv</span> : null}
                </label>
              );
            })}
          </div>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="text-sm font-medium">Designvariante</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            {VARIANT_OPTIONS.map((option) => {
              const checked = appearance.designVariant === option.value;
              return (
                <label
                  key={option.value}
                  className="flex min-w-0 cursor-pointer flex-col gap-3 rounded-md border border-border p-3 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring data-[checked=true]:ring-2 data-[checked=true]:ring-ring"
                  data-checked={checked}
                >
                  <input
                    className="size-4"
                    type="radio"
                    name="design-variant"
                    value={option.value}
                    checked={checked}
                    onChange={() => {
                      void commit({ ...appearance, designVariant: option.value });
                    }}
                  />
                  <ThemePreview variant={option.value} dark={resolved === 'dark'} />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{option.label}</span>
                    <span className="mt-1 block text-sm break-words text-muted-foreground">
                      {option.description}
                    </span>
                  </span>
                  {checked ? <span className="text-sm font-medium">Aktiv</span> : null}
                </label>
              );
            })}
          </div>
        </fieldset>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              void restoreTenant();
            }}
          >
            Standard wiederherstellen
          </Button>
          <p aria-live="polite" className="min-w-0 text-sm break-words text-muted-foreground">
            {status}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
